import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Clock3,
  BadgePercent,
  Ship,
  Building2,
  ArrowRight,
  Play,
  X,
} from "lucide-react";

const whyOGFZPreview = [
  {
    id: 1,
    title: "Round-the-Clock Operations",
    description:
      "OGFZs support 24-hour operations, 365 days a year, enabling investors to run efficient and uninterrupted oil and gas business activities.",
    icon: "clock",
  },
  {
    id: 2,
    title: "75% Duty Rebate",
    description:
      "Investors enjoy strong customs incentives, including a 75% duty rebate that supports cost-effective importation and business expansion.",
    icon: "rebate",
  },
  {
    id: 3,
    title: "Direct Access to Sea",
    description:
      "The zones provide direct access to sea and same-day turnaround of supply vessels, improving logistics speed and operational efficiency.",
    icon: "ship",
  },
  {
    id: 4,
    title: "Strategic Investment Location",
    description:
      "OGFZs are positioned in strategic locations central to West Africa and the sub-Saharan region, supporting procurement, logistics, and regional trade.",
    icon: "building",
  },
];

const iconMap = {
  clock: Clock3,
  rebate: BadgePercent,
  ship: Ship,
  building: Building2,
};

const WhatWeDo = () => {
  const [playOpen, setPlayOpen] = useState(false);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
        {/* Video card */}
        <div className="relative group">
          <div className="relative overflow-hidden rounded-tl-[36px] rounded-tr-[36px] shadow-[0_20px_60px_rgba(0,0,0,0.18)] border border-slate-200">
            <img
              src="/assets/images/bg2.jpeg"
              alt="Oil and Gas Free Zones"
              className="w-full h-[460px] object-cover transition-transform duration-700 group-hover:scale-105"
            />

            {/* OVERLAY */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

            {/* PLAY BUTTON */}
            <button
              onClick={() => setPlayOpen(true)}
              className="absolute inset-0 flex items-center justify-center"
              aria-label="Play video"
            >
              <span className="relative">
                <span className="absolute inset-0 rounded-full bg-[#1f9d4c]/40 animate-ping" />

                <span className="relative w-20 h-20 rounded-full bg-[#1f9d4c] text-white flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:bg-[#d4a017] transition-all duration-300">
                  <Play
                    size={32}
                    fill="white"
                    className="ml-1"
                  />
                </span>
              </span>
            </button>

            {/* TEXT */}
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <p className="text-sm font-semibold uppercase tracking-widest text-[#d4a017]">
                Play Video
              </p>

              <p className="text-xl md:text-2xl font-black mt-1">
                OGFZA at a glance
              </p>
            </div>
          </div>

          {/* Decorative thumbnails strip */}
          <div className="hidden md:flex gap-3 mt-4">
            {[
              "/assets/images/bg1.jpeg",
              "/assets/images/bg3.jpeg",
              "/assets/images/md.jpeg",
            ].map((src) => (
              <div
                key={src}
                className="flex-1 h-20 overflow-hidden rounded-tl-[20px] rounded-tr-[20px] shadow-lg border border-slate-200"
              >
                <img
                  src={src}
                  alt="OGFZA activity"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div>
          <p className="text-[#d4a017] font-semibold tracking-widest text-sm uppercase mb-3">
            Investment Advantage
          </p>

          <h2 className="text-3xl md:text-5xl font-black text-slate-900 leading-tight mb-8">
            Why <span className="text-[#1f9d4c]">OGFZs?</span>
          </h2>

          <div className="space-y-1">
            {whyOGFZPreview.map((item) => {
              const Icon = iconMap[item.icon] || Building2;

              return (
                <div
                  key={item.id}
                  className="flex gap-5 p-5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors group cursor-default rounded-xl"
                >
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-[#1f9d4c]/10 border border-[#1f9d4c]/20 text-[#1f9d4c] flex items-center justify-center group-hover:bg-[#d4a017] group-hover:text-white group-hover:rotate-6 transition-all duration-300">
                    <Icon size={26} />
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-[#06183f] mb-1.5">
                      {item.title}
                    </h3>

                    <p className="text-slate-600 leading-relaxed text-sm">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <Link
            to="/about/why-ogfz"
            className="inline-flex items-center gap-2 mt-6 text-[#06183f] font-bold border-b-2 border-[#06183f] pb-1 hover:gap-3 hover:text-[#d4a017] hover:border-[#d4a017] transition-all duration-300"
          >
            View full Why OGFZs content{" "}
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>

      {/* Video Modal */}
      {playOpen && (
        <div
          onClick={() => setPlayOpen(false)}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl"
          >
            <button
              onClick={() => setPlayOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-[#d4a017] transition-colors"
              aria-label="Close"
            >
              <X size={28} />
            </button>

            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/JqEjEh8X4Pg?autoplay=1"
              title="OGFZA Promo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default WhatWeDo;